import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Plus, Shield, Calendar, ChevronRight, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import DocumentCard from './DocumentCard';
import AddDocumentDialog from './AddDocumentDialog';
import OpenEnrollmentSection from './OpenEnrollmentSection';
import { useDemo } from '@/contexts/DemoContext';

const THEME = {
  button: { bg: '#E8E7E5', hoverBg: '#DDDBD8', text: '#4A4745' },
  empty: { bg: '#F8F7F6', border: '#E5E4E2', icon: '#A8A5A1', title: '#4A4745', text: '#6B6966' },
  header: { title: '#3D3B39', subtitle: '#6B6966' },
  enrollment: {
    bg: '#EEF0F2',
    border: '#D8DCE0',
    iconBg: '#E2E5E8',
    icon: '#5A6570',
    title: '#454B52',
    text: '#6B7280',
  },
  badge: {
    bg: '#F0EFED',
    border: '#E5E4E2',
    text: '#5A5856',
  },
};

const InsuranceTab = ({ searchQuery }) => {
  const { isDemoMode, getDocuments } = useDemo();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showEnrollment, setShowEnrollment] = useState(false);

  useEffect(() => { fetchDocuments(); }, [isDemoMode]);

  const fetchDocuments = async () => {
    try {
      if (isDemoMode) {
        const allDocs = getDocuments() || [];
        const categoryDocs = allDocs.filter(d => d.category_tab === 'insurance' || d.category === 'insurance');
        setDocuments(categoryDocs);
        setLoading(false);
        return;
      }
      const response = await API.get('/documents?category=insurance');
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (isDemoMode) {
      toast.success('Document deleted (demo)');
      fetchDocuments();
      return;
    }
    try {
      await API.delete(`/documents/${id}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const filteredDocs = searchQuery
    ? documents.filter(d => 
        d.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (d.issuer || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (d.insurance_type || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : documents;

  // Group by insurance type
  const groupedDocs = filteredDocs.reduce((acc, doc) => {
    const type = doc.insurance_type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(doc);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-xl" style={{ backgroundColor: '#F0EFED' }} />
        <Skeleton className="h-32 w-full rounded-xl" style={{ backgroundColor: '#F0EFED' }} />
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="flex justify-between items-start sm:items-center mb-6 gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold" style={{ color: THEME.header.title }}>Insurance Documents</h3>
          <p className="text-sm mt-0.5" style={{ color: THEME.header.subtitle }}>All insurance types, policies, and cards</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setShowEnrollment(!showEnrollment)}
            className="shrink-0"
            style={{ 
              borderColor: THEME.enrollment.border,
              color: THEME.enrollment.title,
              backgroundColor: showEnrollment ? THEME.enrollment.bg : 'transparent',
            }}
          >
            <Calendar className="h-4 w-4 mr-2" />
            {showEnrollment ? 'Hide' : 'Open'} Enrollment
          </Button>
          <Button
            onClick={() => setDialogOpen(true)}
            className="shrink-0"
            style={{ backgroundColor: THEME.button.bg, color: THEME.button.text }}
            data-testid="add-insurance-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Insurance
          </Button>
        </div>
      </div>

      {/* Quick Link - What did I pick last year? */}
      <Card 
        className="mb-6 overflow-hidden"
        style={{ 
          backgroundColor: THEME.enrollment.bg, 
          border: `1px solid ${THEME.enrollment.border}`,
          borderRadius: '0.75rem',
        }}
      >
        <CardContent className="p-4">
          <button
            onClick={() => setShowEnrollment(true)}
            className="flex items-center justify-between w-full text-left group"
          >
            <div className="flex items-center gap-3">
              <div 
                className="h-10 w-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: THEME.enrollment.iconBg }}
              >
                <BookOpen className="h-5 w-5" style={{ color: THEME.enrollment.icon }} />
              </div>
              <div>
                <p className="font-medium" style={{ color: THEME.enrollment.title }}>What did I pick last year?</p>
                <p className="text-sm" style={{ color: THEME.enrollment.text }}>Review your past enrollment decisions</p>
              </div>
            </div>
            <ChevronRight 
              className="h-5 w-5 transition-transform group-hover:translate-x-1" 
              style={{ color: THEME.enrollment.icon }} 
            />
          </button>
        </CardContent>
      </Card>

      {/* Open Enrollment Section */}
      {showEnrollment && (
        <div className="mb-8">
          <OpenEnrollmentSection />
        </div>
      )}

      {/* Documents */}
      {filteredDocs.length === 0 ? (
        <div 
          className="text-center py-12 rounded-xl border-2 border-dashed"
          style={{ backgroundColor: THEME.empty.bg, borderColor: THEME.empty.border }}
        >
          <div 
            className="h-14 w-14 mx-auto rounded-xl flex items-center justify-center mb-4"
            style={{ backgroundColor: '#E8E7E5' }}
          >
            <Shield className="h-7 w-7" style={{ color: THEME.empty.icon }} />
          </div>
          <h4 className="text-lg font-medium mb-2" style={{ color: THEME.empty.title }}>Store Insurance Documents</h4>
          <p className="mb-4 max-w-sm mx-auto" style={{ color: THEME.empty.text }}>
            Keep all your insurance cards and policies in one secure place
          </p>
          <Button
            onClick={() => setDialogOpen(true)}
            style={{ backgroundColor: THEME.button.bg, color: THEME.button.text }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Insurance
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedDocs).map(([type, docs]) => (
            <div key={type}>
              <div className="flex items-center gap-2 mb-3">
                <Badge 
                  className="capitalize text-xs font-medium"
                  style={{ 
                    backgroundColor: THEME.badge.bg,
                    color: THEME.badge.text,
                    border: `1px solid ${THEME.badge.border}`,
                  }}
                >
                  {type.replace('_', ' ')}
                </Badge>
                <span className="text-xs" style={{ color: THEME.header.subtitle }}>
                  ({docs.length})
                </span>
              </div>
              <div className="space-y-3">
                {docs.map((doc) => (
                  <DocumentCard key={doc.id} document={doc} onDelete={handleDelete} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <AddDocumentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        category="insurance"
        onSuccess={() => { fetchDocuments(); setDialogOpen(false); }}
      />
    </>
  );
};

export default InsuranceTab;
